import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate, createSearchParams } from "react-router-dom";
import {
  getPaymentList,
  getTenantList,
  getPropertyList,
} from "./Payments.slice";
import Layout from "./Layout";
import Table from "../../../components/Table/Table";
import Badge from "../../../components/Badge";
import Panel from "../../../components/Panel";
import ComboboxAutocomplete from "../../../components/Form/ComboboxAutocomplete";
import Input from "../../../components/Form/Input";
import { Tooltip } from "flowbite-react";
import qs from "qs";
import {
  formatUTCDate,
  formatCurrency,
  makeEnumReadable,
} from "../../../util/string";
import { Payment, PaymentSummaries } from "../../../apis/types";
import { useAppDispatch, useAppSelector } from "../../../shared/redux/hooks";

export interface PaymentsURLParams {
  propertyId: string | undefined
  tenantId: string | undefined
  tenantAccountId: string | undefined
  status: string | undefined
  memo: string | undefined
}

const Payments: React.FC = () => {
  const { search } = useLocation()
  const searchParams = new URLSearchParams(search)
  const dispatch = useAppDispatch()
  const [propertyId, setPropertyId] = useState<string | undefined>(searchParams.get('propertyId') ?? undefined)
  const [tenantId, setTenantId] = useState<string | undefined>(searchParams.get('tenantId') ?? undefined)
  const [status, setStatus] = useState<string | undefined>(searchParams.get('status') ?? undefined)
  const [memo, setMemo] = useState<string | undefined>(searchParams.get('memo') ?? undefined)
  const [listLoading, setListLoading] = useState(true)
  const listLoadGeneration = useRef(0)
  const tenantAccountId = searchParams.get('tenantAccountId') ?? undefined
  const navigate = useNavigate()
  // TODO To reenable the "Trailing 30-day totals" feature import summary here
  const { payments, tenants, properties } = useAppSelector((state) => state.payments)

  // TODO Also remove this flag here and stop the Panel
  // below from being conditionally rendered based on it
  const isTrailing30DayTotalsDisabled = true
  // TODO Also delete this hardcoded InvoiceSummary
  // TODO Also uncomment the call to getInvoiceSummary() on line 51
  const summary: PaymentSummaries | undefined = {
    CANCELLED: { number: 0, amount: 0 },
    CHARGEBACK: { number: 0, amount: 0 },
    FAILED: { number: 0, amount: 0 },
    PENDING: { number: 0, amount: 0 },
    PROCESSING: { number: 0, amount: 0 },
    SUCCEEDED: { number: 0, amount: 0 },
  }
  // TODO Also go to src/features/collections/invoices/Payments.slice.tsx and delete
  // the hardcoded api response for getPaymentSummary()

  useEffect(() => {
    // void dispatch(getPaymentSummary())
    void dispatch(getTenantList())
    void dispatch(getPropertyList())
  }, [dispatch])

  const loadPayments = useCallback(
    (page: number) => {
      const generation = ++listLoadGeneration.current
      setListLoading(true)
      void dispatch(
        getPaymentList({
          page,
          resultsPerPage: 20,
          propertyId,
          tenantId,
          tenantAccountId,
          status,
          memo,
        })
      ).finally(() => {
        if (generation === listLoadGeneration.current) {
          setListLoading(false)
        }
      })
    },
    [dispatch, propertyId, status, tenantAccountId, tenantId, memo]
  )

  useEffect(() => {
    loadPayments(0)
  }, [loadPayments])

  useEffect(() => {
    const nextSearch = qs.stringify(
      { propertyId, tenantId, tenantAccountId, status, memo },
      { addQueryPrefix: true, skipNulls: true }
    )
    if (nextSearch !== search) {
      navigate(`/collections/payments${nextSearch}`, { replace: true })
    }
  }, [propertyId, tenantId, tenantAccountId, status, memo, search, navigate])

  const handlePaginatorClick = (page: number): void => {
    loadPayments(page)
  }

  return (
    <>
      <Layout title="Payments">
        {!isTrailing30DayTotalsDisabled && (
          <Panel header="Trailing 30-day totals" className="mb-5">
            <div className="grid grid-flow-col text-center">
              {(Object.keys(summary ?? {}) as Array<keyof PaymentSummaries>).map((status, index) => (
                <div key={`status-${index}`}>
                  <div className="font-medium">
                    <Badge status={status} />
                  </div>
                  <div className="text-2xl">{summary?.[status]?.number}</div>
                  <div className="">{formatCurrency(summary?.[status]?.amount ?? 0)}</div>
                </div>
              ))}
            </div>
          </Panel>
        )}
        <Panel header="Filters" className="mb-5">
          <div className="grid grid-auto-flow grid-cols-4 gap-3">
            {/* <QuickSearch title="Tenant name" initialValue={quickSearchTerm} handleChange={handleQuickSearchChange} /> */}
            <div>
              <label htmlFor="propertyNames" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Property
              </label>
              {properties !== undefined && (
                <ComboboxAutocomplete
                  labelPlural="Properties"
                  value={propertyId ?? ''}
                  handleChange={(value: string | null | undefined) => setPropertyId(value || undefined)}
                  data={properties}
                />
              )}
            </div>
            <div>
              <label htmlFor="tenantNames" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Tenant
              </label>
              {properties !== undefined && (
                <ComboboxAutocomplete
                  labelPlural="Tenants"
                  value={tenantId ?? ''}
                  handleChange={(value: string | null | undefined) => setTenantId(value || undefined)}
                  data={tenants}
                />
              )}
            </div>
            <div>
              <label htmlFor="statusType" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Status
              </label>
              {properties !== undefined && (
                <ComboboxAutocomplete
                  labelPlural="Statuses"
                  value={status ?? ''}
                  handleChange={(value: string | null | undefined) => setStatus(value || undefined)}
                  data={Object.keys(summary ?? {}).map((status: string) => ({
                    id: status,
                    name: makeEnumReadable(status),
                  }))}
                />
              )}
            </div>
            <div>
              <Input
                label="Memo"
                value={memo ?? ''}
                placeholder="Deposit ID, check #, etc."
                onChange={(e) => setMemo(e.target.value.trim() || undefined)}
              />
            </div>
          </div>
        </Panel>
        <Table<Payment>
            isLoading={listLoading}
            colConfig={[
              {
                label: 'Tenant / Property',
                render: (rec) => (
                  <>
                    <span className="font-medium">{rec.tenant.name}</span>
                    <br />
                    {rec.property.name}
                  </>
                ),
              },
              {
                label: 'Account number',
                render: (rec) => (
                  <Link
                    to={{
                      pathname: `/tenant/tenants/${rec.tenant.id}`,
                      search: `?${createSearchParams({
                        serviceAccountNumber: rec.serviceAccount.number,
                        tab: '4',
                      }).toString()}`,
                    }}
                    className="text-indigo-500 dark:text-indigo-400"
                  >
                    {rec.serviceAccount.number}
                  </Link>
                ),
              },
              {
                label: 'Billing mode',
                render: (rec) => (rec.billingMode ? <Badge status={rec.billingMode} /> : null),
              },
              {
                label: 'Invoice number',
                render: (rec) => rec.invoice.number,
              },
              {
                label: 'Payment number',
                render: (rec) => rec.paymentNumber,
              },
              {
                label: 'Amount',
                render: (rec) => formatCurrency(rec.amount),
              },
              {
                label: 'Payment type / method',
                render: (rec) => {
                  const { providerName, last4 } = rec.paymentMethod
                  const { type } = rec
                  const [paymentType, paymentTypeMethod] = type.split(' - ')
                  const paymentTypeMap: Record<string, string> = {
                    ONE_TIME_PAYMENT: 'One-time payment',
                    AUTOPAY: 'AutoPay',
                  }
                  const paymentTypeMethodMap: Record<string, string> = {
                    CHECKING: 'Checking ACH',
                    SAVINGS: 'Savings ACH',
                    CREDIT_CARD: 'Credit card',
                    CHECK: 'Check',
                    DIRECT_DEPOSIT: 'Direct deposit',
                  }
                  return (
                    <>
                      {paymentTypeMap[paymentType]} - {paymentTypeMethodMap[paymentTypeMethod]}
                      {providerName !== '' && (
                        <>
                          <br />
                          {providerName} {last4 !== null && `(••••${last4})`}
                        </>
                      )}
                    </>
                  )
                },
              },
              {
                label: 'Payment Date',
                render: (rec) => formatUTCDate(new Date(rec.paymentDate)),
              },
              {
                label: 'Posting Date',
                render: (rec) => formatUTCDate(new Date(rec.postingDate)),
              },
              {
                label: 'Memo',
                render: (rec) => rec.memo,
              },
              {
                label: 'Status',
                render: (rec) => {
                  if (rec.status === 'FAILED' && rec.failureReason) {
                    return (
                      <Tooltip
                        content={rec.failureReason}
                        placement="top"
                        className="text-xs z-10 pointer-events-none select-none bg-slate-600 dark:bg-slate-950 border dark:border-0"
                      >
                        <Badge status={rec.status} />
                      </Tooltip>
                    );
                  }
                  return <Badge status={rec.status} />;
                },
              },
            ]}
            data={
              payments ?? {
                results: [],
                total: 0,
                page: 0,
                resultsPerPage: 20,
              }
            }
            handlePaginatorClick={handlePaginatorClick}
          />
      </Layout>
    </>
  )
}

export default Payments;
